package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.Composable
import com.siriusxm.pia.components.jsonView
import com.siriusxm.pia.rest.unifiedaggregator.FieldDiff
import com.siriusxm.pia.views.unifiedaggregator.DiffFieldChangeList
import com.siriusxm.pia.views.unifiedaggregator.EntityDiffStyles
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Displays a summary of changes between two entities.
 *
 * @param diffs List of field differences
 */
@Composable
internal fun DiffSummary(diffs: List<FieldDiff>) {
    val added = diffs.count { it.oldValue == null && it.newValue != null }
    val removed = diffs.count { it.oldValue != null && it.newValue == null }
    val modified = diffs.count { it.oldValue != null && it.newValue != null }

    Div({
        classes(EntityDiffStyles.diffSummary)
    }) {
        when {
            diffs.isEmpty() -> {
                Span({
                    classes(EntityDiffStyles.diffNullValue)
                }) {
                    Text("No differences found")
                }
            }
            else -> {
                val parts = mutableListOf<String>()
                if (modified > 0) parts.add("$modified changed")
                if (added > 0) parts.add("$added added")
                if (removed > 0) parts.add("$removed removed")

                Text("${diffs.size} field${if (diffs.size == 1) "" else "s"}: ${parts.joinToString(", ")}")
            }
        }
    }
}

/**
 * Displays only the fields that differ between two entities.
 *
 * @param diffs List of field differences
 */
@Composable
internal fun DifferencesOnlyView(diffs: List<FieldDiff>) {
    if (diffs.isEmpty()) {
        Div({
            classes(EntityDiffStyles.diffEmpty)
        }) {
            Text("No differences found between authorized and rejected versions")
        }
    } else {
        DiffSummary(diffs)
        DiffFieldChangeList(diffs)
    }
}

/**
 * Displays two entities side by side for comparison.
 *
 * @param authorized The authorized entity (can be null)
 * @param rejected The rejected entity
 */
@Composable
internal fun SideBySideView(authorized: JsonObject?, rejected: JsonObject) {
    Div({
        classes(EntityDiffStyles.sideBySideGrid)
    }) {
        // Left column: Authorized version
        Div({
            classes(EntityDiffStyles.sideBySidePanel)
        }) {
            // Header
            Div({
                classes(EntityDiffStyles.sideBySidePanelHeader)
            }) {
                Text("Authorized Version")
            }

            // Content
            Div({
                classes(EntityDiffStyles.sideBySidePanelContent)
            }) {
                if (authorized != null) {
                    jsonView(authorized.sortedKeys(), hideEmptyOrNull = false)
                } else {
                    Div({
                        classes(EntityDiffStyles.diffEmpty)
                    }) {
                        Text("No published version exists")
                    }
                }
            }
        }

        // Right column: Rejected version
        Div({
            classes(EntityDiffStyles.sideBySidePanel)
        }) {
            // Header
            Div({
                classes(EntityDiffStyles.sideBySidePanelHeader)
            }) {
                Text("Rejected Update")
            }

            // Content
            Div({
                classes(EntityDiffStyles.sideBySidePanelContent)
            }) {
                jsonView(rejected.sortedKeys(), hideEmptyOrNull = false)
            }
        }
    }
}

/**
 * Recursively sorts the keys of a [JsonObject] alphabetically so that two
 * objects with the same fields render in the same order for easy visual comparison.
 */
internal fun JsonObject.sortedKeys(): JsonObject = buildJsonObject {
    for ((key, value) in entries.sortedBy { it.key }) {
        put(key, value.sortedKeysRecursive())
    }
}

private fun JsonElement.sortedKeysRecursive(): JsonElement = when (this) {
    is JsonObject -> sortedKeys()
    is JsonArray -> JsonArray(map { it.sortedKeysRecursive() })
    else -> this
}
